/**
 * Web Serial API 封装 — 用于连接机床设备 (RS232转USB)
 *
 * 浏览器兼容性: Web Serial API 仅在 Chromium 内核浏览器中可用
 * (Chrome 89+, Edge 89+, Opera 75+)
 */

/**
 * 检查浏览器是否支持 Web Serial API
 * @returns {boolean}
 */
export function isSerialSupported() {
    return "serial" in navigator;
}

/**
 * 打开串口连接
 * @param {Object} options
 * @param {number} [options.baudRate=9600] - 波特率
 * @param {number} [options.dataBits=8] - 数据位 (7 或 8)
 * @param {string} [options.parity='none'] - 校验位 ('none', 'even', 'odd')
 * @param {number} [options.stopBits=1] - 停止位 (1 或 2)
 * @returns {Promise<{port: SerialPort, info: Object}>}
 */
export async function openSerialPort(options = {}) {
    const {
        baudRate = 9600,
        dataBits = 8,
        parity = "none",
        stopBits = 1,
    } = options;

    if (!isSerialSupported()) {
        throw new Error("当前浏览器不支持 Web Serial API，请使用 Chrome/Edge 浏览器");
    }

    // 请求用户选择串口
    const port = await navigator.serial.requestPort();
    const info = port.getInfo();

    // 打开串口
    await port.open({ baudRate, dataBits, parity, stopBits });

    return { port, info };
}

/**
 * 关闭串口连接
 * 注意: 如果串口有可读流被锁定，需先释放 reader
 * @param {SerialPort} port
 * @param {ReadableStreamDefaultReader} [reader] - 如果有活动的 reader，先释放
 */
export async function closeSerialPort(port, reader = null) {
    if (reader) {
        try {
            await reader.cancel();
        } catch (_) {
            // reader may already be released
        }
        try {
            reader.releaseLock();
        } catch (_) {
            // lock may already be released
        }
    }

    if (port && port.readable) {
        // 强制关闭 — 即使流被锁定，close() 也会处理
        try {
            await port.close();
        } catch (e) {
            // 如果流被锁定，尝试用更强制的方式
            console.warn("关闭串口时出错:", e.message);
            throw e;
        }
    }
}

/**
 * 读取串口数据 — 返回异步迭代器和 reader
 * 使用方式:
 *   const { reader, readChunk } = await startReading(port);
 *   while (true) {
 *     const data = await readChunk();
 *     // 处理 data (Uint8Array)
 *   }
 *
 * @param {SerialPort} port
 * @returns {{reader: ReadableStreamDefaultReader, readChunk: Function}}
 */
export function createSerialReader(port) {
    if (!port.readable) {
        throw new Error("串口不可读，请先打开连接");
    }

    const reader = port.readable.getReader();

    async function readChunk() {
        const { value, done } = await reader.read();
        if (done) {
            return null; // 流已关闭
        }
        return value; // Uint8Array
    }

    return { reader, readChunk };
}

/**
 * 向串口写入数据
 * @param {SerialPort} port
 * @param {Uint8Array|number[]} data
 */
export async function writeSerial(port, data) {
    if (!port.writable) {
        throw new Error("串口不可写，请先打开连接");
    }

    const writer = port.writable.getWriter();
    try {
        const buffer = data instanceof Uint8Array ? data : new Uint8Array(data);
        await writer.write(buffer);
    } finally {
        writer.releaseLock();
    }
}

/**
 * 获取串口信息的人类可读描述
 * @param {Object} info - port.getInfo() 的返回值
 * @returns {string}
 */
export function formatPortInfo(info) {
    const parts = [];
    if (info.usbVendorId) {
        parts.push(`VID:0x${info.usbVendorId.toString(16).toUpperCase()}`);
    }
    if (info.usbProductId) {
        parts.push(`PID:0x${info.usbProductId.toString(16).toUpperCase()}`);
    }
    return parts.join(" ") || "未知设备";
}
